import { useEffect, useMemo, useState } from "react";
import { ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { apiFetch } from "../../lib/api";
import { T } from "../../ui/tokens";

function extrairFileId(link?: string | null): string | null {
  if (!link) return null;
  return link.match(/\/d\/([A-Za-z0-9_-]+)/)?.[1]
    || link.match(/[?&]id=([A-Za-z0-9_-]+)/)?.[1]
    || null;
}

interface Props {
  driveLink?: string | null;
  empresa: string;
  titulo: string;
}

export default function DrivePreview({ driveLink, empresa, titulo }: Props) {
  const fileId = useMemo(() => extrairFileId(driveLink), [driveLink]);
  const [tentativa, setTentativa] = useState(0);
  const [url, setUrl] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [formatoSemPreview, setFormatoSemPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!fileId) return;
    const controller = new AbortController();
    let objectUrl: string | null = null;
    setLoading(true);
    setErro(null);
    setFormatoSemPreview(null);
    setUrl(null);
    apiFetch(`/api/documentos/drive-preview?empresa=${encodeURIComponent(empresa)}&file_id=${encodeURIComponent(fileId)}`, {
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          const detail = typeof body?.detail === "string" ? body.detail : body?.detail?.msg;
          throw new Error(detail || "Não foi possível carregar o documento.");
        }
        const contentType = (response.headers?.get("content-type") || "")
          .split(";", 1)[0]
          .trim()
          .toLowerCase();
        const podeMostrarNoBrowser = contentType === "application/pdf"
          || contentType.startsWith("image/");
        if (!podeMostrarNoBrowser) {
          setFormatoSemPreview(contentType || "formato desconhecido");
          return;
        }
        objectUrl = URL.createObjectURL(await response.blob());
        setUrl(objectUrl);
      })
      .catch((error) => {
        if (error?.name !== "AbortError") setErro(error?.message || "Não foi possível carregar o documento.");
      })
      .finally(() => setLoading(false));
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fileId, empresa, tentativa]);

  if (!fileId) {
    return <div style={mensagem}>O link deste documento não é válido.</div>;
  }
  if (loading) {
    return <div style={mensagem}><Loader2 size={20} style={{ animation: "spin-extrato .8s linear infinite" }} />A carregar documento…</div>;
  }
  if (formatoSemPreview) {
    return (
      <div style={mensagem}>
        <div style={{ fontWeight: 700, color: T.text }}>Pré-visualização não disponível neste formato.</div>
        <div style={{ maxWidth: 430, lineHeight: 1.5 }}>
          O ficheiro original não será aberto automaticamente para evitar downloads inesperados.
        </div>
        {driveLink && <button type="button" onClick={() => window.open(driveLink, "_blank", "noopener,noreferrer")} style={botao}>
          <ExternalLink size={13} /> Abrir ficheiro original
        </button>}
      </div>
    );
  }
  if (erro || !url) {
    return (
      <div style={mensagem}>
        <div style={{ fontWeight: 700, color: T.text }}>Não foi possível mostrar o documento.</div>
        <div style={{ maxWidth: 430, lineHeight: 1.5 }}>{erro}</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
          <button type="button" onClick={() => setTentativa((valor) => valor + 1)} style={botao}>
            <RefreshCw size={13} /> Tentar novamente
          </button>
          {driveLink && <button type="button" onClick={() => window.open(driveLink, "_blank", "noopener,noreferrer")} style={botao}>
            <ExternalLink size={13} /> Abrir e iniciar sessão no Google
          </button>}
        </div>
      </div>
    );
  }
  return <iframe title={titulo} src={url} style={{ width: "100%", height: "100%", border: "none" }} />;
}

const mensagem: React.CSSProperties = {
  height: "100%", minHeight: 220, padding: 24, boxSizing: "border-box",
  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
  gap: 10, textAlign: "center", color: T.textMid, fontSize: 12.5,
};

const botao: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 10px",
  border: `1px solid ${T.border}`, borderRadius: 7, background: T.surface,
  color: T.text, fontSize: 11.5, fontWeight: 700, cursor: "pointer",
};
